import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { 
    PlusCircle, 
    Pencil, 
    X,
    CheckCircle 
} from 'lucide-react';

const ProductModal = ({ isOpen, onClose, onSubmit, product = null }) => {
    const [formData, setFormData] = useState({
        type: 'goods',
        name: '',
        productId: '',
        description: '',
        hsnSac: '',
        rate: '',
        unit: 'pcs',
        gstRate: 18,
        stock: 0
    });
    const [error, setError] = useState('');

    useEffect(() => {
        if (product) {
            setFormData({
                type: product.type || 'goods',
                name: product.name || '',
                productId: product.productId || '',
                description: product.description || '',
                hsnSac: product.hsnSac || '',
                rate: product.rate || '',
                unit: product.unit || 'pcs',
                gstRate: product.gstRate !== undefined ? product.gstRate : 18,
                stock: product.stock || 0
            });
        } else {
            setFormData({
                type: 'goods',
                name: '',
                productId: '',
                description: '',
                hsnSac: '',
                rate: '',
                unit: 'pcs',
                gstRate: 18,
                stock: 0
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!formData.name || !formData.productId || !formData.hsnSac || formData.rate === '' || (formData.type === 'goods' && formData.stock === '')) {
            setError('Please fill all required fields');
            return;
        }

        if (parseFloat(formData.rate) <= 0) {
            setError('Rate must be greater than 0');
            return;
        }

        if (formData.type === 'goods' && parseInt(formData.stock) < 0) {
            setError('Stock cannot be negative');
            return;
        }

        onSubmit({
            ...formData,
            rate: parseFloat(formData.rate),
            gstRate: parseInt(formData.gstRate),
            stock: formData.type === 'service' ? 0 : (parseInt(formData.stock) || 0)
        });

        // Reset form
        setFormData({
            type: 'goods',
            name: '',
            description: '',
            hsnSac: '',
            rate: '',
            unit: 'pcs',
            gstRate: 18,
            stock: 0
        });
        setError('');
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={product ? 'Edit Product' : 'Add New Product'}
            footer={
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={onClose}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <X size={18} /> Cancel
                    </button>
                    <button 
                        type="submit" 
                        className="btn btn-primary" 
                        onClick={handleSubmit}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        {product ? <Pencil size={18} /> : <PlusCircle size={18} />}
                        {product ? 'Update Product' : 'Add Product'}
                    </button>
                </div>
            }
        >
            <form onSubmit={handleSubmit}>
                {error && (
                    <div style={{ padding: '10px', backgroundColor: '#fee', color: '#c33', borderRadius: '5px', marginBottom: '15px' }}>
                        {error}
                    </div>
                )}

                <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ marginBottom: '10px', display: 'block' }}>Type *</label>
                    <div style={{ display: 'flex', gap: '20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="type"
                                value="goods"
                                checked={formData.type === 'goods'}
                                onChange={handleChange}
                            />
                            Goods
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="type"
                                value="service"
                                checked={formData.type === 'service'}
                                onChange={handleChange}
                            />
                            Service
                        </label>
                    </div>
                </div>

                <div className="form-group">
                    <label className="form-label">Product/Service Name *</label>
                    <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter product or service name"
                        required
                    />
                </div>

                <div className="form-group" >
                    <label className="form-label">Product ID (SKU) *</label>
                    <input
                        type="text"
                        name="productId"
                        className="form-control"
                        value={formData.productId}
                        onChange={handleChange}
                        placeholder="e.g., PROD-001"
                        maxLength={20}
                        required
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                        name="description"
                        className="form-control"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Enter product description (optional)"
                        rows="2"
                    />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">HSN/SAC Code *</label>
                        <input
                            type="text"
                            name="hsnSac"
                            className="form-control"
                            value={formData.hsnSac}
                            onChange={handleChange}
                            placeholder="e.g., 998314"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Unit</label>
                        <input
                            type="text"
                            name="unit"
                            className="form-control"
                            value={formData.unit}
                            onChange={handleChange}
                            placeholder="e.g., pcs, kg, hrs"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Rate (₹) *</label>
                        <input
                            type="number"
                            name="rate"
                            className="form-control"
                            value={formData.rate}
                            onChange={handleChange}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            required
                        />
                    </div>

                    {formData.type === 'goods' ? (
                        <div className="form-group">
                            <label className="form-label">Stock Quantity *</label>
                            <input
                                type="number"
                                name="stock"
                                className="form-control"
                                value={formData.stock}
                                onChange={handleChange}
                                placeholder="0"
                                min="0"
                                required
                            />
                        </div>
                    ) : (
                        <div className="form-group"></div>
                    )}
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">GST Rate (%) *</label>
                        <select
                            name="gstRate"
                            className="form-control"
                            value={formData.gstRate}
                            onChange={handleChange}
                            required
                        >
                            <option value="0">0%</option>
                            <option value="5">5%</option>
                            <option value="12">12%</option>
                            <option value="18">18%</option>
                            <option value="28">28%</option>
                        </select>
                    </div>
                </div>
            </form>
        </Modal>
    );
};

export default ProductModal;
